import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { TrendingUp, DollarSign, FileText, Users, Calendar, ChevronDown, Award, Building2, Loader2, Pencil, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import DirectLegChart from '../components/DirectLegChart';
import { PageHeader } from '../components/dashboard/PageHeader';
import { StatCard } from '../components/dashboard/StatCard';

import { Sensitive } from '../components/Sensitive';
import { AnnuityProductionSection } from '../components/production/AnnuityProductionSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const avgApPerPolicy = (data) => {
  const ap = Number(data?.issued_ap) || 0;
  const n = Number(data?.total_policies) || 0;
  return n > 0 ? Math.round(ap / n) : 0;
};

export default function Production() {
  const { user, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');
  const [dateRange, setDateRange] = useState('year');
  
  const [startDate, setStartDate] = useState(format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  const [personalData, setPersonalData] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [directLegsData, setDirectLegsData] = useState(null);
  
  // Agent carrier-breakdown drilldown (team-tab modal)
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentCarrierData, setAgentCarrierData] = useState(null);
  const [agentCarrierLoading, setAgentCarrierLoading] = useState(false);

  // Admin-only inline edit dialog (correct CSV-import errors)
  const isAdmin = user?.role === 'admin';
  const [editingPolicy, setEditingPolicy] = useState(null);

  // Custom-date-range in-progress selection (lives across clicks until user
  // picks a second, distinct date — at which point the range is applied).
  const [pendingRange, setPendingRange] = useState(undefined);

  // Team-tab agent search + sort
  const [teamSearch, setTeamSearch] = useState('');
  const [teamSort, setTeamSort] = useState('issued'); // 'issued' | 'policies'

  // Company-tab agent search + sort + pagination
  const [companySearch, setCompanySearch] = useState('');
  const [companySort, setCompanySort] = useState('issued'); // 'issued' | 'policies' | 'avg'
  const [companyPage, setCompanyPage] = useState(0);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build params: only include date filters when they have a value
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      if (activeTab === 'personal') {
        const response = await axios.get(`${API}/production/personal`, {
          ...getAuthHeader(),
          params
        });
        setPersonalData(response.data);
      } else if (activeTab === 'company') {
        const response = await axios.get(`${API}/production/company`, {
          ...getAuthHeader(),
          params
        });
        setCompanyData(response.data);
      } else {
        const [teamResponse, legsResponse] = await Promise.all([
          axios.get(`${API}/production/team`, {
            ...getAuthHeader(),
            params
          }),
          axios.get(`${API}/production/direct-legs`, {
            ...getAuthHeader(),
            params
          })
        ]);
        setTeamData(teamResponse.data);
        setDirectLegsData(legsResponse.data);
      }
    } catch (error) {
      toast.error('Failed to load production data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrillDown = async (legId, start, end) => {
    try {
      const response = await axios.get(`${API}/production/leg-breakdown/${legId}`, {
        ...getAuthHeader(),
        params: { start_date: start, end_date: end }
      });
      return response.data;
    } catch (error) {
      toast.error('Failed to load leg breakdown');
      console.error(error);
      return null;
    }
  };

  const prepareChartData = (clients) => {
    if (!clients || clients.length === 0) return [];
    
    const dataMap = {};
    clients.forEach(client => {
      const date = client.date_issued || client.date_submitted || client.created_at?.split('T')[0];
      if (!date) return;
      
      if (!dataMap[date]) {
        dataMap[date] = { date, issued: 0 };
      }
      
      const premium = client.premium || client.annual_premium || 0;
      
      // ATLAS only tracks ISSUED business
      if (client.status === 'Issued') {
        dataMap[date].issued += premium;
      }
    });
    
    return Object.values(dataMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getQuickDateRange = (range) => {
    const today = new Date();
    let start, end;
    
    switch (range) {
      case 'today':
        start = end = format(today, 'yyyy-MM-dd');
        break;
      case 'week':
        start = format(subDays(today, 7), 'yyyy-MM-dd');
        end = format(today, 'yyyy-MM-dd');
        break;
      case 'month':
        start = format(startOfMonth(today), 'yyyy-MM-dd');
        end = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case 'quarter':
        start = format(subDays(today, 90), 'yyyy-MM-dd');
        end = format(today, 'yyyy-MM-dd');
        break;
      case 'year':
        start = format(new Date(today.getFullYear(), 0, 1), 'yyyy-MM-dd');
        end = format(today, 'yyyy-MM-dd');
        break;
      case 'all':
        // No date filter — show ALL production records
        start = '';
        end = '';
        break;
      default:
        return;
    }
    
    setDateRange(range);
    setStartDate(start);
    setEndDate(end);
  };

  const currentData = activeTab === 'personal' ? personalData : activeTab === 'company' ? companyData : teamData;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Label is an ISO date string (yyyy-MM-dd). Display in US format.
      let displayLabel = label;
      try {
        if (label && /^\d{4}-\d{2}-\d{2}/.test(label)) {
          displayLabel = format(new Date(label + 'T00:00:00'), 'MM/dd/yyyy');
        }
      } catch {
        displayLabel = label;
      }
      return (
        <div className="bg-white border border-[#E9EDF7] rounded-lg shadow-lg px-3 py-2">
          <p className="text-xs text-[#A3AED0] mb-1">{displayLabel}</p>
          {payload.map((p, i) => (
            <p key={p.name || p.dataKey || i} className="text-sm font-semibold" style={{ color: p.color }}>
              {p.name}: ${p.value?.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Drilldown: fetch an agent's production by-carrier when a row is clicked
  const openAgentCarriers = async (agentId, agentName) => {
    if (!agentId) return;
    setSelectedAgent({ id: agentId, name: agentName });
    setAgentCarrierData(null);
    setAgentCarrierLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      const res = await axios.get(
        `${API}/production/agent/${agentId}/by-carrier`,
        { ...getAuthHeader(), params }
      );
      setAgentCarrierData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load agent carrier breakdown');
      setSelectedAgent(null);
    } finally {
      setAgentCarrierLoading(false);
    }
  };

  const closeAgentCarriers = () => {
    setSelectedAgent(null);
    setAgentCarrierData(null);
  };

  // Admin-only: open edit dialog + save handler + refetch
  const openEditPolicy = (policy) => {
    if (!isAdmin) return;
    setEditingPolicy(policy);
  };

  const closeEditPolicy = () => setEditingPolicy(null);

  const handleSavePolicy = async (policyId, changes) => {
    try {
      const res = await axios.patch(
        `${API}/admin/production/${policyId}`,
        changes,
        getAuthHeader()
      );
      toast.success('Policy updated');
      setEditingPolicy(null);
      // Refetch data so the UI reflects the correction
      await fetchData();
      // If an agent drilldown is open, refresh it too
      if (selectedAgent?.id) {
        openAgentCarriers(selectedAgent.id, selectedAgent.name);
      }
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#2447D6] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="production-page">
      {/* Compact Header with Date Selector */}
      <PageHeader
        title="Production"
        subtitle="Track sales performance"
        actions={
          <div className="flex flex-wrap items-center gap-1.5 rounded-2xl border border-[#E9EDF7] bg-white p-1.5 shadow-[0px_10px_24px_rgba(112,144,176,0.14)]">
          {['today', 'week', 'month', 'quarter', 'year', 'all'].map((range) => (
            <button
              key={range}
              data-testid={`production-daterange-${range}`}
              onClick={() => {
                setPendingRange(undefined);
                getQuickDateRange(range);
              }}
              className={`px-4 py-2 text-sm font-bold rounded-xl transition-all capitalize ${
                dateRange === range
                  ? 'bg-gradient-to-r from-[#4318FF] to-[#2447D6] text-white shadow-[0px_6px_14px_rgba(67,24,255,0.35)] scale-[1.03]'
                  : 'text-[#2B3674]/70 hover:bg-[#F4F7FE] hover:text-[#1E3FBF]'
              }`}
            >
              {range}
            </button>
          ))}
          {/* Custom date range */}
          <Popover
            onOpenChange={(open) => {
              if (!open) setPendingRange(undefined);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="production-daterange-custom"
                className={`px-4 py-2 text-sm font-bold rounded-xl transition-all flex items-center gap-1.5 ${
                  dateRange === 'custom'
                    ? 'bg-gradient-to-r from-[#01B574] to-[#43E97B] text-white shadow-[0px_6px_14px_rgba(1,181,116,0.35)] scale-[1.03]'
                    : 'text-[#2B3674]/70 hover:bg-[#F4F7FE] hover:text-[#01B574]'
                }`}
              >
                <Calendar className="h-4 w-4" />
                {dateRange === 'custom'
                  ? `${format(new Date(startDate + 'T00:00:00'), 'MMM d, yyyy')} – ${format(new Date(endDate + 'T00:00:00'), 'MMM d, yyyy')}`
                  : 'Custom'}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-auto p-0"
              data-testid="production-custom-range-popover"
            >
              <CalendarComponent
                mode="range"
                numberOfMonths={2}
                defaultMonth={pendingRange?.from || (dateRange === 'custom' ? new Date(startDate + 'T00:00:00') : new Date(startDate + 'T00:00:00'))}
                selected={
                  pendingRange ?? (dateRange === 'custom'
                    ? { from: new Date(startDate + 'T00:00:00'), to: new Date(endDate + 'T00:00:00') }
                    : undefined)
                }
                onSelect={(range) => {
                  // Track in-progress selection so the first click is visible
                  setPendingRange(range);
                  // Apply only when user has picked TWO distinct dates
                  if (
                    range?.from &&
                    range?.to &&
                    range.from.getTime() !== range.to.getTime()
                  ) {
                    const newStart = format(range.from, 'yyyy-MM-dd');
                    const newEnd = format(range.to, 'yyyy-MM-dd');
                    setStartDate(newStart);
                    setEndDate(newEnd);
                    setDateRange('custom');
                  }
                }}
              />
              <div className="flex items-center justify-between gap-2 p-3 border-t border-[#E9EDF7]">
                <span className="text-[11px] text-[#A3AED0]">
                  {pendingRange?.from && !pendingRange?.to
                    ? 'Now click the end date.'
                    : pendingRange?.from && pendingRange?.to && pendingRange.from.getTime() === pendingRange.to.getTime()
                      ? 'Click a different end date to apply the range.'
                      : 'Click the start date, then the end date.'}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPendingRange(undefined);
                    getQuickDateRange('year');
                  }}
                  className="text-[11px] font-medium text-[#2B3674]/80 hover:text-[#1E3FBF]"
                >
                  Reset
                </button>
              </div>
            </PopoverContent>
          </Popover>
          </div>
        }
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#E9EDF7] p-1 h-auto">
          <TabsTrigger 
            value="personal"
            className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm"
          >
            <DollarSign className="h-4 w-4 mr-1.5" />
            Personal
          </TabsTrigger>
          <TabsTrigger 
            value="team"
            className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm"
          >
            <Users className="h-4 w-4 mr-1.5" />
            Team
          </TabsTrigger>
          <TabsTrigger 
            value="company"
            data-testid="company-tab-trigger"
            className="data-[state=active]:bg-white data-[state=active]:text-[#1E3FBF] data-[state=active]:shadow-sm px-4 py-2 text-sm"
          >
            <Building2 className="h-4 w-4 mr-1.5" />
            Company
          </TabsTrigger>
        </TabsList>

        {/* Personal Production Tab */}
        <TabsContent value="personal" className="space-y-4 mt-4">
          {/* Compact Stats Row — ATLAS only tracks ISSUED business */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Issued Life AP"
              value={<Sensitive>{`$${Math.round(currentData?.issued_life_ap ?? currentData?.issued_ap ?? 0).toLocaleString()}`}</Sensitive>}
              icon={TrendingUp}
              color="green"
              index={0}
              testId="personal-stat-issued-ap"
            />
            <StatCard
              title="Issued Annuity"
              value={<Sensitive>{`$${Math.round(currentData?.issued_annuity_ap || 0).toLocaleString()}`}</Sensitive>}
              icon={PiggyBank}
              color="teal"
              index={1}
              testId="personal-stat-issued-annuity"
            />
            <StatCard
              title="Issued Policies"
              value={<Sensitive>{currentData?.total_policies || 0}</Sensitive>}
              icon={FileText}
              color="purple"
              index={2}
              testId="personal-stat-policies"
            />
            <StatCard
              title="Avg AP per Policy"
              value={<Sensitive>{`$${avgApPerPolicy(currentData).toLocaleString()}`}</Sensitive>}
              icon={DollarSign}
              color="blue"
              index={3}
              testId="personal-stat-avg-ap"
            />
          </div>

          {/* Chart — issued AP only */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-white">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-emerald-50">
                    <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                  </div>
                  <CardTitle className="text-sm font-semibold text-[#2B3674]">Issued AP Trend</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-2">
                {personalData?.clients && personalData.clients.length > 0 ? (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={prepareChartData(personalData.clients)} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                        <defs>
                          <linearGradient id="issuedGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                          axisLine={{ stroke: '#e2e8f0' }}
                          tickLine={false}
                          tickFormatter={(value) => format(new Date(value + 'T00:00:00'), 'MM/dd')}
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: '#94a3b8' }}
                          axisLine={false}
                          tickLine={false}
                          tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                          width={45}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="issued" stroke="#10b981" strokeWidth={2} fill="url(#issuedGradient)" name="Issued" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-56 flex items-center justify-center text-[#A3AED0] text-sm">
                    No issued production for selected period
                  </div>
                )}
                <div className="flex items-center justify-center gap-6 mt-3 text-xs text-[#A3AED0]">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 bg-emerald-500 rounded"></div>
                    <span>Issued</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Issued AP by Carrier — Personal */}
          <CarrierBreakdownCard
            title="Issued AP by Carrier"
            subtitle="Your issued production broken down by carrier"
            breakdown={personalData?.by_carrier || []}
          />

          <AnnuityProductionSection
            data={personalData}
            policies={(personalData?.clients || []).filter((c) => (c.product_type || '').toLowerCase() === 'annuity')}
            canEdit={isAdmin}
            onEdit={openEditPolicy}
            testId="personal-annuity-section"
          />

          {/* Policy Details Table — Personal */}
          <PolicyDetailsCard
            policies={personalData?.clients || []}
            canEdit={isAdmin}
            onEdit={openEditPolicy}
          />
        </TabsContent>

        {/* Team Production Tab */}
        <TabsContent value="team" className="space-y-4 mt-4">
          {/* Stats Row — ATLAS only tracks ISSUED business */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Team Issued Life AP"
              value={<Sensitive>{`$${Math.round(currentData?.issued_life_ap ?? currentData?.issued_ap ?? 0).toLocaleString()}`}</Sensitive>}
              icon={TrendingUp}
              color="green"
              index={0}
              testId="team-stat-issued-ap"
            />
            <StatCard
              title="Team Issued Annuity"
              value={<Sensitive>{`$${Math.round(currentData?.issued_annuity_ap || 0).toLocaleString()}`}</Sensitive>}
              icon={PiggyBank}
              color="teal"
              index={1}
              testId="team-stat-issued-annuity"
            />
            <StatCard
              title="Issued Policies"
              value={<Sensitive>{currentData?.total_policies || 0}</Sensitive>}
              icon={FileText}
              color="purple"
              index={2}
              testId="team-stat-policies"
            />
            <StatCard
              title="Avg AP per Policy"
              value={<Sensitive>{`$${avgApPerPolicy(currentData).toLocaleString()}`}</Sensitive>}
              icon={DollarSign}
              color="blue"
              index={3}
              testId="team-stat-avg-ap"
            />
          </div>

          {/* Analytics + Chart Grid */}
          <div className="grid lg:grid-cols-5 gap-4">
            {/* Analytics Cards */}
            {teamData?.analytics && (
              <div className="lg:col-span-2 space-y-3">
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 }}>
                  <Card className="bg-gradient-to-br from-blue-50 to-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-[#A3AED0]">Issuing Agents</p>
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-[#2B3674]">{teamData.analytics.issuing_agents}</span>
                            <span className="text-sm text-[#A3AED0]">/ {teamData.analytics.total_active_agents}</span>
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-blue-600">{teamData.analytics.issuing_agent_percentage}%</span>
                      </div>
                      <div className="mt-2 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-blue-500 h-full rounded-full" style={{ width: `${teamData.analytics.issuing_agent_percentage}%` }} />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}>
                  <Card className="bg-gradient-to-br from-emerald-50 to-white">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-100">
                          <DollarSign className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-[#A3AED0]">Avg Issued AP per Writer</p>
                          <span className="text-xl font-bold text-emerald-600" data-testid="avg-issued-per-writer">
                            ${(teamData.analytics.avg_issued_per_writer || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                          <p className="text-[10px] text-[#A3AED0] mt-0.5">
                            {teamData.analytics.issuing_agents || 0} writer{teamData.analytics.issuing_agents === 1 ? '' : 's'} (≥ $1 issued)
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

              </div>
            )}

            {/* Chart */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ delay: 0.2 }}
              className={teamData?.analytics ? 'lg:col-span-3' : 'lg:col-span-5'}
            >
              <Card className="bg-white h-full">
                <CardHeader className="pb-2 pt-4 px-4">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-emerald-50">
                      <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                    </div>
                    <CardTitle className="text-sm font-semibold text-[#2B3674]">Team Issued AP</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-4 pt-2">
                  {teamData?.clients && teamData.clients.length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={prepareChartData(teamData.clients)} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" className="" />
                          <XAxis 
                            dataKey="date"
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                            tickLine={false}
                            tickFormatter={(value) => {
                              try {
                                const d = new Date(value);
                                return isNaN(d.getTime()) ? String(value) : format(d, 'MMM d');
                              } catch { return String(value); }
                            }}
                          />
                          <YAxis 
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => `$${value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}`}
                            width={45}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="issued" fill="#10b981" name="Issued" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-[#A3AED0] text-sm">
                      No issued production for selected period
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Direct Legs Production Breakdown Chart - Enhanced */}
          <DirectLegChart 
            directLegsData={directLegsData} 
            onDrillDown={handleDrillDown}
            startDate={startDate}
            endDate={endDate}
          />

          {/* Leaderboard */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="bg-white">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-amber-50">
                      <Award className="h-3.5 w-3.5 text-amber-600" />
                    </div>
                    <CardTitle className="text-sm font-semibold text-[#2B3674]">
                      Team Agents
                    </CardTitle>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#E9EDF7] text-[#A3AED0]">
                      {(teamData?.agents || []).length}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={teamSearch}
                    onChange={(e) => setTeamSearch(e.target.value)}
                    placeholder="Search agent name..."
                    data-testid="team-agent-search"
                    className="text-xs px-3 py-1.5 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] placeholder:text-[#A3AED0] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40 w-full sm:w-56"
                  />
                </div>
                <div className="text-[11px] text-[#A3AED0] mt-1.5">
                  Click any agent to see their issued-policy list & carrier breakdown.
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-2">
                {(() => {
                  let filtered = (teamData?.agents || []).filter((a) =>
                    !teamSearch || (a.agent_name || '').toLowerCase().includes(teamSearch.toLowerCase())
                  );
                  // Apply sort — ATLAS only tracks issued business
                  const agentAvg = (a) => (a.policies || 0) > 0 ? Math.round((a.issued_ap || 0) / a.policies) : 0;
                  filtered = [...filtered].sort((a, b) => {
                    if (teamSort === 'policies') return (b.policies || 0) - (a.policies || 0);
                    if (teamSort === 'avg') return agentAvg(b) - agentAvg(a);
                    return (b.issued_ap || 0) - (a.issued_ap || 0);
                  });
                  if (!teamData?.agents || teamData.agents.length === 0) {
                    return (
                      <div className="py-8 text-center text-[#A3AED0] text-sm">
                        No team data available
                      </div>
                    );
                  }
                  if (filtered.length === 0) {
                    return (
                      <div className="py-8 text-center text-[#A3AED0] text-sm">
                        No agents match your search.
                      </div>
                    );
                  }
                  return (
                    <>
                      {/* Sort pills */}
                      <div className="flex items-center gap-1 mb-3">
                        <span className="text-[11px] text-[#A3AED0] mr-1">Sort by:</span>
                        {[
                          { key: 'issued', label: 'Issued AP' },
                          { key: 'policies', label: 'Policies' },
                          { key: 'avg', label: 'Avg AP' },
                        ].map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => setTeamSort(s.key)}
                            data-testid={`team-sort-${s.key}`}
                            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                              teamSort === s.key
                                ? 'bg-[#2B3674] text-white'
                                : 'bg-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-200'
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
                        {filtered.map((agent, index) => {
                          const avg = agentAvg(agent);
                          const primaryValue = teamSort === 'policies'
                            ? agent.policies
                            : teamSort === 'avg'
                              ? avg
                              : agent.issued_ap;
                          const primaryLabel = teamSort === 'policies'
                            ? `polic${agent.policies === 1 ? 'y' : 'ies'}`
                            : teamSort === 'avg'
                              ? 'avg/policy'
                              : 'issued';
                          return (
                            <button
                              type="button"
                              key={agent.agent_id}
                              onClick={() => openAgentCarriers(agent.agent_id, agent.agent_name)}
                              data-testid={`team-agent-row-${agent.agent_id}`}
                              className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-md hover:border-[#3B82F6] cursor-pointer ${
                                index === 0
                                  ? 'bg-amber-50 border-amber-200' 
                                  : 'bg-[#F4F7FE] border-[#E9EDF7]'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                index === 0 ? 'bg-amber-500 text-white' :
                                index === 1 ? 'bg-slate-400 text-white' :
                                index === 2 ? 'bg-amber-700 text-white' :
                                'bg-slate-200 text-[#2B3674]/80'
                              }`}>
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-[#2B3674] truncate">
                                    {agent.agent_name}
                                  </span>
                                  {agent.agent_id === user.id && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-[#1E3FBF]">You</span>
                                  )}
                                </div>
                                <span className="text-xs text-[#A3AED0]" data-testid={`team-agent-avg-${agent.agent_id}`}>
                                  <Sensitive>{agent.policies} issued polic{agent.policies === 1 ? 'y' : 'ies'}{avg > 0 ? ` • $${avg.toLocaleString()} avg/policy` : ''}</Sensitive>
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-[#2B3674]">
                                  <Sensitive>{teamSort === 'policies' ? `${primaryValue}` : `$${(primaryValue || 0).toLocaleString()}`}</Sensitive>
                                  <span className="text-[10px] text-[#A3AED0] ml-1">{primaryLabel}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </motion.div>

          {/* Issued AP by Carrier — Team-wide */}
          <CarrierBreakdownCard
            title="Team Issued AP by Carrier"
            subtitle="Company-wide production broken down by carrier"
            breakdown={teamData?.by_carrier || []}
          />

          <AnnuityProductionSection
            data={teamData}
            policies={(teamData?.clients || []).filter((c) => (c.product_type || '').toLowerCase() === 'annuity')}
            canEdit={isAdmin}
            onEdit={openEditPolicy}
            title="Team Issued Annuity Production"
            testId="team-annuity-section"
          />
        </TabsContent>

        {/* Company Production Tab — every agent, agency-wide */}
        <TabsContent value="company" className="space-y-4 mt-4" data-testid="company-tab-content">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
            <Card className="bg-white">
              <CardHeader className="pb-2 pt-4 px-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-blue-50">
                      <Building2 className="h-3.5 w-3.5 text-[#1E3FBF]" />
                    </div>
                    <CardTitle className="text-sm font-semibold text-[#2B3674]">
                      Top Producers — Companywide
                    </CardTitle>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#E9EDF7] text-[#A3AED0]">
                      {(companyData?.agents || []).length}
                    </span>
                  </div>
                  <input
                    type="text"
                    value={companySearch}
                    onChange={(e) => { setCompanySearch(e.target.value); setCompanyPage(0); }}
                    placeholder="Search any agent..."
                    data-testid="company-agent-search"
                    className="text-xs px-3 py-1.5 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] placeholder:text-[#A3AED0] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40 w-full sm:w-56"
                  />
                </div>
                <div className="text-[11px] text-[#A3AED0] mt-1.5">
                  Every agent in the company, ranked by issued paid AP for the selected date range.
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 pt-2">
                {(() => {
                  const all = companyData?.agents || [];
                  const agentAvg = (a) => (a.policies || 0) > 0 ? Math.round((a.issued_ap || 0) / a.policies) : 0;
                  // Sort the FULL list by the selected metric (rank = position here)
                  const sorted = [...all].sort((a, b) => {
                    if (companySort === 'policies') return (b.policies || 0) - (a.policies || 0);
                    if (companySort === 'avg') return agentAvg(b) - agentAvg(a);
                    return (b.issued_ap || 0) - (a.issued_ap || 0);
                  });
                  const filtered = sorted.filter((a) =>
                    !companySearch || (a.agent_name || '').toLowerCase().includes(companySearch.toLowerCase())
                  );
                  const PAGE_SIZE = 20;
                  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
                  const page = Math.min(companyPage, totalPages - 1);
                  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
                  if (all.length === 0) {
                    return (
                      <div className="py-8 text-center text-[#A3AED0] text-sm">
                        No production data available
                      </div>
                    );
                  }
                  if (filtered.length === 0) {
                    return (
                      <div className="py-8 text-center text-[#A3AED0] text-sm" data-testid="company-no-match">
                        No agents match your search.
                      </div>
                    );
                  }
                  return (
                    <>
                      {/* Sort pills */}
                      <div className="flex items-center gap-1 mb-3">
                        <span className="text-[11px] text-[#A3AED0] mr-1">Sort by:</span>
                        {[
                          { key: 'issued', label: 'Issued AP' },
                          { key: 'policies', label: 'Policies' },
                          { key: 'avg', label: 'Avg AP' },
                        ].map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={() => { setCompanySort(s.key); setCompanyPage(0); }}
                            data-testid={`company-sort-${s.key}`}
                            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                              companySort === s.key
                                ? 'bg-[#2B3674] text-white'
                                : 'bg-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-200'
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                      <div className="space-y-2">
                        {pageRows.map((agent) => {
                          const rank = sorted.indexOf(agent) + 1;
                          const avg = agentAvg(agent);
                          const primaryValue = companySort === 'policies'
                            ? agent.policies
                            : companySort === 'avg'
                              ? avg
                              : agent.issued_ap;
                          const primaryLabel = companySort === 'policies'
                            ? `polic${agent.policies === 1 ? 'y' : 'ies'}`
                            : companySort === 'avg'
                              ? 'avg/policy'
                              : 'issued';
                          return (
                            <div
                              key={agent.agent_id}
                              data-testid={`company-agent-row-${agent.agent_id}`}
                              className={`flex items-center gap-3 p-3 rounded-lg border ${
                                rank === 1
                                  ? 'bg-amber-50 border-amber-200'
                                  : 'bg-[#F4F7FE] border-[#E9EDF7]'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                rank === 1 ? 'bg-amber-500 text-white' :
                                rank === 2 ? 'bg-slate-400 text-white' :
                                rank === 3 ? 'bg-amber-700 text-white' :
                                'bg-slate-200 text-[#2B3674]/80'
                              }`}>
                                {rank}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm text-[#2B3674] truncate">
                                    {agent.agent_name}
                                  </span>
                                  {agent.agent_id === user.id && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-[#1E3FBF]">You</span>
                                  )}
                                </div>
                                <span className="text-xs text-[#A3AED0]">
                                  <Sensitive>{agent.policies} issued polic{agent.policies === 1 ? 'y' : 'ies'}{avg > 0 ? ` • $${avg.toLocaleString()} avg/policy` : ''}</Sensitive>
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-bold text-[#2B3674]">
                                  <Sensitive>{companySort === 'policies' ? `${primaryValue}` : `$${(primaryValue || 0).toLocaleString()}`}</Sensitive>
                                  <span className="text-[10px] text-[#A3AED0] ml-1">{primaryLabel}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Pagination — 20 per page */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-4" data-testid="company-pagination">
                          <button
                            type="button"
                            onClick={() => setCompanyPage(Math.max(0, page - 1))}
                            disabled={page === 0}
                            data-testid="company-page-prev"
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#E9EDF7] bg-white text-[#2B3674] hover:bg-[#F4F7FE] disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            ← Previous
                          </button>
                          <div className="flex items-center gap-1.5">
                            {Array.from({ length: totalPages }, (_, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setCompanyPage(i)}
                                data-testid={`company-page-${i + 1}`}
                                className={`w-7 h-7 text-xs font-bold rounded-lg transition-colors ${
                                  i === page
                                    ? 'bg-gradient-to-r from-[#4318FF] to-[#2447D6] text-white'
                                    : 'bg-[#E9EDF7] text-[#2B3674]/80 hover:bg-slate-200'
                                }`}
                              >
                                {i + 1}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setCompanyPage(Math.min(totalPages - 1, page + 1))}
                            disabled={page >= totalPages - 1}
                            data-testid="company-page-next"
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#E9EDF7] bg-white text-[#2B3674] hover:bg-[#F4F7FE] disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Next →
                          </button>
                        </div>
                      )}
                      <div className="text-center text-[11px] text-[#A3AED0] mt-2">
                        Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length} agents
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Agent drilldown dialog — shown when a team agent row is clicked */}
      <Dialog open={!!selectedAgent} onOpenChange={(open) => { if (!open) closeAgentCarriers(); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="agent-carrier-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedAgent?.name} — Production Detail
            </DialogTitle>
          </DialogHeader>
          {agentCarrierLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-[#2447D6]" />
            </div>
          )}
          {!agentCarrierLoading && agentCarrierData && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#F4F7FE] rounded-lg p-3 text-center">
                  <div className="text-xs text-[#A3AED0]">Issued AP</div>
                  <div className="text-lg font-bold text-emerald-600">
                    <Sensitive>${agentCarrierData.issued_ap.toLocaleString()}</Sensitive>
                  </div>
                </div>
                <div className="bg-[#F4F7FE] rounded-lg p-3 text-center">
                  <div className="text-xs text-[#A3AED0]">Issued Policies</div>
                  <div className="text-lg font-bold text-violet-600">
                    {agentCarrierData.total_policies || 0}
                  </div>
                </div>
              </div>
              {agentCarrierData.by_carrier && agentCarrierData.by_carrier.length > 0 ? (
                <div className="rounded-lg border divide-y divide-[#E9EDF7]">
                  {agentCarrierData.by_carrier.map((c) => (
                    <div
                      key={c.carrier}
                      className="flex items-center justify-between px-4 py-3"
                      data-testid={`agent-carrier-row-${c.carrier}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Building2 className="h-4 w-4 text-[#A3AED0] shrink-0" />
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-[#2B3674] truncate">
                            {c.carrier}
                          </div>
                          <div className="text-xs text-[#A3AED0]">
                            {c.policies} issued polic{c.policies === 1 ? 'y' : 'ies'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-emerald-600">
                          <Sensitive>${c.issued_ap.toLocaleString()}</Sensitive>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center text-[#A3AED0] text-sm">
                  No issued production for this agent in the selected range.
                </div>
              )}

              {/* Per-agent policy list — admin can click pencil icon to correct premium/status */}
              {agentCarrierData.policies && agentCarrierData.policies.length > 0 && (
                <PolicyDetailsCard
                  policies={agentCarrierData.policies}
                  canEdit={isAdmin}
                  onEdit={openEditPolicy}
                  title="Issued Policies"
                  subtitle={isAdmin ? 'Click the pencil to correct a policy' : undefined}
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin-only edit dialog — mounted at page root so it sits above all other dialogs */}
      {editingPolicy && (
        <EditPolicyDialog
          policy={editingPolicy}
          onClose={closeEditPolicy}
          onSave={handleSavePolicy}
        />
      )}
    </div>
  );
}

/* ---------- Shared inline component ---------- */
function CarrierBreakdownCard({ title, subtitle, breakdown }) {
  const hasData = breakdown && breakdown.length > 0;
  const totalIssued = hasData ? breakdown.reduce((sum, c) => sum + (c.issued_ap || 0), 0) : 0;
  
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
      <Card className="bg-white">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[#A3AED0]" />
            <CardTitle className="text-sm font-semibold text-[#2B3674]">{title}</CardTitle>
          </div>
          {subtitle && (
            <div className="text-xs text-[#A3AED0] pl-6">{subtitle}</div>
          )}
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-2">
          {hasData ? (
            <div className="rounded-lg border divide-y divide-[#E9EDF7] overflow-hidden">
              {breakdown.map((c) => {
                const pct = totalIssued > 0 ? Math.round(((c.issued_ap || 0) / totalIssued) * 100) : 0;
                return (
                  <div
                    key={c.carrier}
                    className="px-4 py-3"
                    data-testid={`carrier-row-${c.carrier}`}
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-medium text-sm text-[#2B3674] truncate">
                          {c.carrier}
                        </span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#E9EDF7] text-[#A3AED0]">
                          {c.policies} issued
                        </span>
                      </div>
                      <div className="text-right shrink-0 pl-3">
                        <div className="text-sm font-bold text-emerald-600">
                          <Sensitive>${(c.issued_ap || 0).toLocaleString()}</Sensitive>
                        </div>
                      </div>
                    </div>
                    {/* Progress bar — relative share of total issued */}
                    <div className="h-1.5 bg-[#E9EDF7] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-[#A3AED0] text-sm">
              No production records in the selected range.
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}


/* ---------- Personal Policy Details Table — ATLAS only tracks ISSUED business ---------- */
function PolicyDetailsCard({ policies, canEdit = false, onEdit, title = 'My Issued Policies', subtitle }) {
  const [search, setSearch] = useState('');

  // Sort: most recent issued_date (fallback to submitted_date) first
  const sorted = [...(policies || [])].sort((a, b) => {
    const da = a.date_issued || a.date_submitted || a.created_at || '';
    const db = b.date_issued || b.date_submitted || b.created_at || '';
    return db.localeCompare(da);
  });

  const filtered = sorted.filter((p) => {
    if (search) {
      const q = search.toLowerCase();
      const hay = `${p.insured_name || ''} ${p.carrier || ''} ${p.policy_number || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const formatDate = (val) => {
    if (!val) return '—';
    try {
      if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
        return format(new Date(val + 'T00:00:00'), 'MM/dd/yyyy');
      }
      return format(new Date(val), 'MM/dd/yyyy');
    } catch {
      return val;
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
      <Card className="bg-white" data-testid="personal-policy-details-card">
        <CardHeader className="pb-2 pt-4 px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-emerald-50">
                <FileText className="h-3.5 w-3.5 text-emerald-600" />
              </div>
              <CardTitle className="text-sm font-semibold text-[#2B3674]">
                {title}
              </CardTitle>
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-[#E9EDF7] text-[#A3AED0]">
                {sorted.length}
              </span>
            </div>

            {/* Search */}
            <input
              type="text"
              placeholder="Search client, carrier, policy #..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="personal-policy-search"
              className="text-xs px-3 py-1.5 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] placeholder:text-[#A3AED0] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40 w-full sm:w-56"
            />
          </div>
          {subtitle && (
            <div className="text-xs text-[#A3AED0] mt-1">{subtitle}</div>
          )}
        </CardHeader>

        <CardContent className="px-4 pb-4 pt-2">
          {filtered.length === 0 ? (
            <div className="py-10 text-center text-[#A3AED0] text-sm">
              {sorted.length === 0
                ? 'No issued policies in the selected period.'
                : 'No policies match your search.'}
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-[#E9EDF7]">
              <table className="w-full text-xs">
                <thead className="bg-[#F4F7FE]">
                  <tr>
                    <th className="text-left py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide">Client</th>
                    <th className="text-left py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide">Carrier</th>
                    <th className="text-right py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide">Annual Premium</th>
                    <th className="text-left py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide hidden sm:table-cell">Policy #</th>
                    <th className="text-left py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide hidden md:table-cell">Issued Date</th>
                    {canEdit && (
                      <th className="text-right py-2 px-3 text-[#A3AED0] font-medium uppercase tracking-wide w-16">
                        Edit
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p, idx) => (
                    <tr
                      key={p.id || `${p.policy_number}-${idx}`}
                      data-testid={`personal-policy-row-${p.policy_number || idx}`}
                      className="border-t border-slate-100 hover:bg-[#F4F7FE]"
                    >
                      <td className="py-2.5 px-3 text-[#2B3674] font-medium">
                        {p.insured_name || '—'}
                      </td>
                      <td className="py-2.5 px-3 text-[#2B3674]/80">
                        {p.carrier || '—'}
                        {p.product_type === 'Annuity' && (
                          <span className="ml-1.5 rounded-full bg-cyan-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-cyan-700">Annuity</span>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right text-[#2B3674] font-medium">
                        <Sensitive>${Number(p.annual_premium || 0).toLocaleString()}</Sensitive>
                      </td>
                      <td className="py-2.5 px-3 text-[#A3AED0] hidden sm:table-cell font-mono text-[11px]">
                        {p.policy_number || '—'}
                      </td>
                      <td className="py-2.5 px-3 text-[#A3AED0] hidden md:table-cell">
                        {formatDate(p.date_issued || p.date_submitted)}
                      </td>
                      {canEdit && (
                        <td className="py-2.5 px-3 text-right">
                          <button
                            type="button"
                            onClick={() => onEdit && onEdit(p)}
                            data-testid={`policy-edit-btn-${p.policy_number || idx}`}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#A3AED0] hover:text-[#1E3FBF] hover:bg-blue-50 transition-colors"
                            title="Edit policy"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
}


/* ---------- Admin-Only Edit Policy Dialog ---------- */
function EditPolicyDialog({ policy, onClose, onSave }) {
  const [premium, setPremium] = useState(policy?.annual_premium ?? '');
  const [status, setStatus] = useState(policy?.display_status === 'Closed' ? 'Closed' : 'Issued');
  const [carrier, setCarrier] = useState(policy?.carrier || '');
  const [insured, setInsured] = useState(policy?.insured_name || '');
  const [issuedDate, setIssuedDate] = useState('');
  const [productType, setProductType] = useState(policy?.product_type === 'Annuity' ? 'Annuity' : 'Life');
  const [saving, setSaving] = useState(false);

  // Normalize whatever date string the backend returns into the YYYY-MM-DD
  // format required by <input type="date">. Accepts ISO ("2026-04-21"),
  // ISO-with-time, or MM/DD/YYYY and falls back to '' on anything unparseable.
  const toIsoDate = (val) => {
    if (!val) return '';
    if (typeof val !== 'string') return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return val.slice(0, 10);
    const m = val.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const [, mm, dd, yyyy] = m;
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    return '';
  };

  useEffect(() => {
    setPremium(policy?.annual_premium ?? '');
    setStatus(policy?.display_status === 'Closed' ? 'Closed' : 'Issued');
    setCarrier(policy?.carrier || '');
    setInsured(policy?.insured_name || '');
    setIssuedDate(toIsoDate(policy?.date_issued || policy?.issued_date || ''));
    setProductType(policy?.product_type === 'Annuity' ? 'Annuity' : 'Life');
  }, [policy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const premiumNum = Number(premium);
    if (Number.isNaN(premiumNum) || premiumNum < 0) {
      toast.error('Annual premium must be a positive number');
      return;
    }
    setSaving(true);
    try {
      await onSave(policy.id, {
        annual_premium: premiumNum,
        status,
        carrier: carrier.trim(),
        insured_name: insured.trim(),
        // Empty string clears the issued_date; backend will store '' which the
        // table renders as "—". Status decides whether the date is meaningful.
        issued_date: issuedDate,
        product_type: productType,
      });
    } catch {
      // toast already shown inside onSave
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!policy} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="edit-policy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[#2447D6]" />
            Edit Policy
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="text-[11px] text-[#A3AED0] font-mono pb-1 border-b border-slate-100">
            Agent: <span className="text-[#2B3674]">{policy?.agent_name || '—'}</span>
            {' · '}
            Policy #: <span className="text-[#2B3674]">{policy?.policy_number || '—'}</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
              Client Name
            </label>
            <input
              type="text"
              value={insured}
              onChange={(e) => setInsured(e.target.value)}
              data-testid="edit-policy-insured"
              className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
              Carrier
            </label>
            <input
              type="text"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              data-testid="edit-policy-carrier"
              className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
                Annual Premium ($)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={premium}
                onChange={(e) => setPremium(e.target.value)}
                data-testid="edit-policy-premium"
                className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="edit-policy-status"
                className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
              >
                <option value="Issued">Issued</option>
                <option value="Closed">Closed (excluded from totals)</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
              Product Type
            </label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              data-testid="edit-policy-product-type"
              className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
            >
              <option value="Life">Life</option>
              <option value="Annuity">Annuity</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-[#2B3674]/80 mb-1">
              Issued Date
            </label>
            <input
              type="date"
              value={issuedDate}
              onChange={(e) => setIssuedDate(e.target.value)}
              data-testid="edit-policy-issued-date"
              className="w-full text-sm px-3 py-2 rounded-md border border-[#E9EDF7] bg-white text-[#2B3674] focus:outline-none focus:ring-2 focus:ring-[#2447D6]/40"
            />
            <p className="mt-1 text-[10px] text-[#A3AED0]">
              Leave blank to clear. Date filters in reports use this value first, then submitted date.
            </p>
          </div>

          <div className="rounded-md bg-[#F4F7FE] px-3 py-2 text-[11px] text-[#A3AED0]">
            ATLAS only tracks <em>Issued</em> business. Marking a policy <em>Closed</em> removes it from all production totals.
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              data-testid="edit-policy-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="edit-policy-save"
              className="bg-[#1E3FBF] hover:bg-[#1E3FBF] text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
